export class AudioPlayer {
  private static activeAudios = new Set<HTMLAudioElement>();

  public static async play(src: string, options?: { volume?: number; playbackRate?: number }) {
    return new Promise<void>((resolve) => {
      const audio = new Audio(src);
      audio.preload = 'auto';
      if (options?.volume !== undefined) {
        audio.volume = options.volume;
      }

      if (options?.playbackRate !== undefined) {
        audio.playbackRate = options.playbackRate;
      }

      this.activeAudios.add(audio);
      const release = () => {
        this.activeAudios.delete(audio);
        resolve();
      };

      audio.onended = release;
      audio.onerror = release;

      void audio.play();
    });
  }
}

export type TAudioEffect =
  | 'BUTTON_CLICK'
  | 'BUTTON_HOVER'
  | 'DICE_RESULT'
  | 'SHORT_SLIDE'
  | 'PAGE_FLIP'
  | 'INCORRECT'
  | 'CORRECT'
  | 'WARNING'
  | 'TIMER_5S'
  | 'MODAL_POPUP'
  | 'BUBBLE_POP'
  | 'POP'
  | 'HINT'
  | 'TOAST_POSITIVE'
  | 'TOAST_INFO'
  | 'STAR_RATING_FINAL';

/**
 * When enabled, decorative effects (pop-ins, button hovers, slide transitions) are muted.
 * Feedback and action sounds (correct/incorrect, drops, dice, timer, ...) always play.
 */
export const REDUCED_AUDIO = true;

export class AudioEffectPlayer {
  public static async play(effect: TAudioEffect, options?: { decorative?: boolean }) {
    if (options?.decorative && REDUCED_AUDIO) {
      return;
    }

    const url = {
      BUTTON_CLICK: '/audio/button-click.mp3',
      BUTTON_HOVER: '/audio/button-hover.mp3',
      DICE_RESULT: '/audio/dice-result.wav',
      SHORT_SLIDE: '/audio/slide-in.mp3',
      PAGE_FLIP: '/audio/page-flip.wav',
      INCORRECT: '/audio/fout-antwoord.mp3',
      CORRECT: '/audio/goed-antwoord.mp3',
      WARNING: '/audio/warning.wav',
      TIMER_5S: '/audio/timer.mp3',
      MODAL_POPUP: '/audio/modal-popup.mp3',
      BUBBLE_POP: '/audio/bubble-pop.mp3',
      POP: '/audio/pop.wav',
      HINT: '/audio/hint.mp3',
      TOAST_POSITIVE: '/audio/toast-positive.mp3',
      TOAST_INFO: '/audio/toast-info.mp3',
      STAR_RATING_FINAL: '/audio/star-rating-final.mp3',
    }[effect];

    await AudioPlayer.play(url);
  }

  private static starRatingContext: AudioContext | undefined;
  private static starRatingTimeouts: ReturnType<typeof setTimeout>[] = [];

  private static playStarPing(frequency: number) {
    const ctx = (this.starRatingContext ??= new AudioContext());
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);

    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.22);
  }

  /**
   * Star rating chime: plays `starCount` ascending-pitch pings (100ms apart), and layers
   * STAR_RATING_FINAL on top of the last ping. Matches the CODE SoundFX prototype exactly.
   * `onTick` fires in lockstep with each ping (e.g. to light up a star as its ping plays).
   */
  public static playStarRating(starCount: number, onTick?: (index: number) => void) {
    this.starRatingTimeouts.forEach((t) => clearTimeout(t));
    this.starRatingTimeouts = [];

    for (let i = 0; i < starCount; i++) {
      const timeout = setTimeout(() => {
        this.playStarPing(523 * Math.pow(2, (i / 4) * 0.75));
        onTick?.(i);

        if (i === starCount - 1) {
          void this.play('STAR_RATING_FINAL');
        }
      }, i * 100);

      this.starRatingTimeouts.push(timeout);
    }
  }
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | undefined = undefined;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | undefined = undefined;

  public async startRecording(): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      throw new Error('Recording is already in progress');
    }

    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create MediaRecorder instance
      this.mediaRecorder = new MediaRecorder(this.stream);
      this.audioChunks = [];

      // Collect audio data chunks
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // Start recording
      this.mediaRecorder.start();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }

  public stopRecording(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('No active recording to stop'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        // Create blob from chunks
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });

        // Stop all tracks to release microphone
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
        }

        if (audioBlob.size === 0) {
          reject(new Error('Recording is too short to play back'));
          return;
        }

        const audioUrl = URL.createObjectURL(audioBlob);

        resolve(audioUrl);
      };

      this.mediaRecorder.stop();
    });
  }
}
